import { useMemo, useState } from "react";
import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { Combobox } from "@/ui";

const CREATE = "__create__";

export interface TypeaheadOption {
    id: string;
    label: string;
    sublabel?: string | null;
    /** Optional section heading; options with a group render under it. */
    group?: string;
}

interface TypeaheadProps {
    value: string;                       // selected option id, or "" for none
    options: TypeaheadOption[];
    onChange: (id: string) => void;
    placeholder?: string;
    emptyLabel?: string;                 // label for the "clear / none" choice
    /** Include the "clear / none" row (default true). Off for add-only pickers. */
    allowEmpty?: boolean;
    maxResults?: number;
    /** Shown as a last row when the typed name isn't an exact match. */
    onCreate?: (label: string) => void;
    creating?: boolean;
}

/** Item shape fed to Base UI's Combobox. The `{ value, label }` keys let it
 *  derive the input's display text automatically; `sublabel` is ours. */
interface Item {
    value: string;
    label: string;
    sublabel?: string | null;
}

interface Group { value: string; items: Item[] }

/**
 * Search-as-you-type combobox. Replaces a native <select> for large lists
 * (thousands of companies / contacts) where a dropdown is unusable. Built on
 * the ui/Combobox design-system primitive (Base UI), which handles keyboard
 * nav, ARIA, and portalling so the popup can't be clipped by a modal.
 */
export default function Typeahead({
    value,
    options,
    onChange,
    placeholder,
    emptyLabel = "None",
    allowEmpty = true,
    maxResults = 50,
    onCreate,
    creating = false,
}: TypeaheadProps) {
    const [query, setQuery] = useState("");
    const createName = query.trim();
    const exactMatch = createName.length > 0 && options.some(
        (o) => o.label.toLowerCase() === createName.toLowerCase(),
    );
    const showCreate = Boolean(onCreate && createName && !exactMatch);

    // A synthetic empty item keeps the "clear / none" choice always selectable.
    const items = useMemo<Item[]>(
        () => [
            ...(allowEmpty ? [{ value: "", label: emptyLabel, sublabel: null }] : []),
            ...options.map((o) => ({ value: o.id, label: o.label, sublabel: o.sublabel })),
            ...(showCreate ? [{ value: CREATE, label: `Create “${createName}”`, sublabel: null }] : []),
        ],
        [options, emptyLabel, allowEmpty, showCreate, createName],
    );
    // Grouped mode when any option carries a group: sections in first-seen
    // order, items A→Z inside each.
    const groups = useMemo<Group[] | null>(() => {
        if (!options.some((o) => o.group)) return null;
        const by = new Map<string, Item[]>();
        for (const o of options) {
            const g = o.group ?? "Other";
            (by.get(g) ?? by.set(g, []).get(g)!).push({ value: o.id, label: o.label, sublabel: o.sublabel });
        }
        for (const list of by.values()) list.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
        return [...by.entries()].map(([value, items]) => ({ value, items }));
    }, [options]);

    // `undefined` = nothing chosen, so the placeholder shows. A non-empty id
    // resolves to its item (or undefined if stale) to drive the display text.
    const selected = value === "" ? undefined : items.find((i) => i.value === value);

    const renderItem = (item: Item) => (
        <Combobox.Item key={item.value || "__none__"} value={item}>
            {item.value === "" ? (
                <span className="text-muted">{item.label}</span>
            ) : item.value === CREATE ? (
                <span className="min-w-0 truncate">{creating ? "Creating…" : item.label}</span>
            ) : (
                <>
                    <span className="min-w-0 truncate">{item.label}</span>
                    {item.sublabel && (
                        <span className="shrink-0 whitespace-nowrap text-xs text-muted">
                            · {item.sublabel}
                        </span>
                    )}
                </>
            )}
        </Combobox.Item>
    );

    return (
        <Combobox.Root
            items={groups ?? items}
            value={selected}
            onValueChange={(item: Item | null) => {
                if (item?.value === CREATE) {
                    if (createName && !creating) onCreate?.(createName);
                    return;
                }
                onChange(item?.value ?? "");
            }}
            onInputValueChange={(next: string) => setQuery(next)}
            autoHighlight={Boolean(onCreate)}
            itemToStringLabel={(item: Item) => item.label}
            limit={maxResults}
            filter={(item: Item, q) => {
                if (item.value === CREATE) return true;
                // Hide "none" while typing so Enter picks a match (or Create).
                if (item.value === "") return !onCreate || !q.trim();
                const needle = q.trim().toLowerCase();
                if (!needle) return true;
                return (
                    item.label.toLowerCase().includes(needle) ||
                    (item.sublabel?.toLowerCase().includes(needle) ?? false)
                );
            }}
        >
            <div className="relative w-full">
                <Combobox.Input placeholder={placeholder} disabled={creating} />
            </div>
            <Combobox.Content>
                <Combobox.Empty>No matches</Combobox.Empty>
                <Combobox.List>
                    {groups
                        ? (group: Group) => (
                            <BaseCombobox.Group key={group.value} items={group.items} className="pb-1">
                                <BaseCombobox.GroupLabel className="px-2 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[.1em] text-muted">
                                    {group.value}
                                </BaseCombobox.GroupLabel>
                                <BaseCombobox.Collection>{renderItem}</BaseCombobox.Collection>
                            </BaseCombobox.Group>
                        )
                        : renderItem}
                </Combobox.List>
            </Combobox.Content>
        </Combobox.Root>
    );
}
